import React, {useState, useEffect} from 'react';

import {useMutation, useSubscription} from '@apollo/client/react';
import {X, Mail, Lock, AlertCircle, User, Briefcase, ArrowLeftRight, Check, XCircle} from 'lucide-react';
import {PhoneInput} from '../PhoneInput';
import {gql} from 'types/__generated__';
import type {GetUserQuery} from 'types/__generated__/graphql';

const SET_USER_EMAIL = gql(`
  mutation SetUserEmail($oldEmail: String!, $newEmail: String!) {
    setUserEmail(oldEmail: $oldEmail, newEmail: $newEmail)
  }
`);

const CHANGE_PASSWORD = gql(`
  mutation ChangePassword($username: String!, $password: String!) {
    changePassword(username: $username, password: $password)
  }
`);

// Admin-facing mutation: edits the *target* user. `updateUserAccountInfo` (no `ForUser`)
// always writes to the logged-in user, so it must not be used from this modal.
// UserAccountInfoInput carries firstName/lastName, so name and profile save in one call.
const UPDATE_USER_ACCOUNT_INFO_FOR_USER = gql(`
  mutation UpdateUserAccountInfoForUser($userId: UUID!, $input: UserAccountInfoInput!) {
    updateUserAccountInfoForUser(userId: $userId, input: $input)
  }
`);

const USER_SUBSCRIPTION = gql(`
  subscription UserUpdatedForEdit($userId: UUID!) {
    user(userId: $userId) {
      id
      username
      email
      firstName
      lastName
      name
      address {
        streetAddress
        city
        postalCode
        country
        mobilePhone
        businessPhone
      }
      workInformation {
        companyName
        department
        jobTitle
      }
    }
  }
`);

interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  address?: {
    streetAddress?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    mobilePhone?: string;
    businessPhone?: string;
  };
  workInformation?: {
    companyName?: string;
    department?: string;
    jobTitle?: string;
  };
}

/**
 * The user as GetUser selects it. `User` previously resolved to the lucide-react icon
 * component imported above, so this prop accepted an icon and rejected the query result.
 */
type EditableUser = NonNullable<GetUserQuery['getUser']>;

interface EditUserModalProps {
  user: EditableUser;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Helper function to parse names from email or username
function parseNameFromString(str: string): {firstName: string; lastName: string} {
  if (!str) return {firstName: '', lastName: ''};

  // Remove email domain if present
  const localPart = str.split('@')[0];

  // Split on common separators: dot, underscore, dash
  const parts = localPart.split(/[\.\-_]+/).filter((part) => part.length > 0);

  if (parts.length === 0) return {firstName: '', lastName: ''};
  if (parts.length === 1) return {firstName: capitalize(parts[0]), lastName: ''};

  // For 2 parts: could be "firstname lastname" or "firstname-lastname" (double last name)
  // Default to treating as double last name (e.g., "janik-cempel" -> "" "Janik Cempel")
  // unless there's a dot separator which typically means "firstname.lastname"
  if (parts.length === 2) {
    const separator = localPart.match(/[\.\-_]/)?.[0];

    // Dot separator usually means firstname.lastname
    if (separator === '.') {
      return {
        firstName: capitalize(parts[0]),
        lastName: capitalize(parts[1]),
      };
    }

    // Dash or underscore: treat as double last name (firstname empty, full string as lastname)
    return {
      firstName: '',
      lastName: parts.map(capitalize).join(' '),
    };
  }

  // For 3+ parts: first part as first name, rest as last name
  return {
    firstName: capitalize(parts[0]),
    lastName: parts.slice(1).map(capitalize).join(' '),
  };
}

function capitalize(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export default function EditUserModal({user, isOpen, onClose, onSuccess}: EditUserModalProps) {
  const [activeTab, setActiveTab] = useState<'profile' | 'work' | 'email' | 'password'>('profile');

  // Profile fields
  const [firstName, setFirstName] = useState(user.firstName || '');
  const [lastName, setLastName] = useState(user.lastName || '');

  // Suggested names from email/username parsing
  const [suggestedFirstName, setSuggestedFirstName] = useState('');
  const [suggestedLastName, setSuggestedLastName] = useState('');
  const [showNameSuggestion, setShowNameSuggestion] = useState(false);
  const [streetAddress, setStreetAddress] = useState(user.address?.streetAddress || '');
  const [city, setCity] = useState(user.address?.city || '');
  const [postalCode, setPostalCode] = useState(user.address?.postalCode || '');
  const [country, setCountry] = useState(user.address?.country || '');
  const [mobilePhone, setMobilePhone] = useState(user.address?.mobilePhone || '');
  const [businessPhone, setBusinessPhone] = useState(user.address?.businessPhone || '');

  // Work fields
  const [companyName, setCompanyName] = useState(user.workInformation?.companyName || '');
  const [department, setDepartment] = useState(user.workInformation?.department || '');
  const [jobTitle, setJobTitle] = useState(user.workInformation?.jobTitle || '');

  // Email & password fields
  const [newEmail, setNewEmail] = useState(user.email);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pendingUpdate, setPendingUpdate] = useState(false);

  // Parse names when modal opens if firstName/lastName are empty
  useEffect(() => {
    if (isOpen && !user.firstName && !user.lastName) {
      // Try email first, then username
      const sourceString = user.email || user.username;
      const parsed = parseNameFromString(sourceString);

      if (parsed.firstName || parsed.lastName) {
        setSuggestedFirstName(parsed.firstName);
        setSuggestedLastName(parsed.lastName);
        setShowNameSuggestion(true);
      }
    }
  }, [isOpen, user.email, user.username, user.firstName, user.lastName]);

  // Subscribe to user updates
  const {data: subscriptionData} = useSubscription(USER_SUBSCRIPTION, {
    variables: {userId: user.id},
    skip: !isOpen, // Only subscribe when modal is open
  });

  // Update form fields when subscription receives updates
  useEffect(() => {
    if (subscriptionData?.user) {
      const updatedUser = subscriptionData.user;
      console.log('📨 User subscription update received:', updatedUser);

      setFirstName(updatedUser.firstName || '');
      setLastName(updatedUser.lastName || '');
      setStreetAddress(updatedUser.address?.streetAddress || '');
      setCity(updatedUser.address?.city || '');
      setPostalCode(updatedUser.address?.postalCode || '');
      setCountry(updatedUser.address?.country || '');
      setMobilePhone(updatedUser.address?.mobilePhone || '');
      setBusinessPhone(updatedUser.address?.businessPhone || '');
      setCompanyName(updatedUser.workInformation?.companyName || '');
      setDepartment(updatedUser.workInformation?.department || '');
      setJobTitle(updatedUser.workInformation?.jobTitle || '');
      setNewEmail(updatedUser.email);

      // If we were waiting for an update, close the modal now
      if (pendingUpdate) {
        console.log('✅ Update confirmed via subscription, closing modal');
        setPendingUpdate(false);
        setSuccess('Updated successfully!');
        setTimeout(() => {
          onClose();
        }, 500);
      }
    }
  }, [subscriptionData, pendingUpdate, onClose]);

  const [setUserEmail, {loading: emailLoading}] = useMutation(SET_USER_EMAIL);
  const [changePassword, {loading: passwordLoading}] = useMutation(CHANGE_PASSWORD);
  const [updateUserAccountInfoForUser, {loading: accountLoading}] = useMutation(UPDATE_USER_ACCOUNT_INFO_FOR_USER);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newEmail || !newEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    if (newEmail === user.email) {
      setError('New email is the same as the current email');
      return;
    }

    try {
      await setUserEmail({
        variables: {
          oldEmail: user.email,
          newEmail: newEmail,
        },
      });
      setPendingUpdate(true);
      setSuccess('Updating email...');
    } catch (err: any) {
      setError(err.message || 'Failed to update email');
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      await changePassword({
        variables: {
          username: user.username,
          password: newPassword,
        },
      });
      setSuccess('Password set successfully!');
      setNewPassword('');
      setConfirmPassword('');
      // Password changes don't affect user fields, close immediately
      setTimeout(() => onClose(), 500);
    } catch (err: any) {
      setError(err.message || 'Failed to set password');
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // Name and profile fields all live on UserAccountInfoInput, so they save in one call.
      // Omitted fields are left untouched; the name is only sent when at least one half is
      // filled in, matching the previous behaviour (clearing both names was never possible).
      const input: Record<string, string | null | undefined> = {
        streetAddress: streetAddress.trim() || undefined,
        city: city.trim() || undefined,
        postalCode: postalCode.trim() || undefined,
        country: country.trim() || undefined,
        mobilePhone: mobilePhone.trim() || undefined,
        businessPhone: businessPhone.trim() || undefined,
        companyName: companyName.trim() || undefined,
        department: department.trim() || undefined,
        jobTitle: jobTitle.trim() || undefined,
      };

      if (firstName.trim() || lastName.trim()) {
        input.firstName = firstName.trim() || null;
        input.lastName = lastName.trim() || null;
      }

      if (Object.values(input).some((value) => value !== undefined)) {
        console.log('Sending updateUserAccountInfoForUser for:', user.username, input);
        await updateUserAccountInfoForUser({
          variables: {userId: user.id, input},
        });
      }

      setPendingUpdate(true);
      setSuccess('Saving changes...');
    } catch (err: any) {
      console.error('Failed to update profile:', err);
      setError(err.message || 'Failed to update profile');
    }
  };

  const handleWorkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await updateUserAccountInfoForUser({
        variables: {
          userId: user.id,
          input: {
            companyName: companyName || undefined,
            department: department || undefined,
            jobTitle: jobTitle || undefined,
          },
        },
      });
      setPendingUpdate(true);
      setSuccess('Saving work information...');
    } catch (err: any) {
      setError(err.message || 'Failed to update work information');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit User</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {user.name} (@{user.username})
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <X size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'profile'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-b-2 border-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <User size={16} className="inline mr-2" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab('work')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'work'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-b-2 border-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Briefcase size={16} className="inline mr-2" />
            Work Info
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'email'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-b-2 border-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Mail size={16} className="inline mr-2" />
            Email
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'password'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-b-2 border-blue-600'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Lock size={16} className="inline mr-2" />
            Password
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start">
              <AlertCircle size={20} className="text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileSubmit}>
              <div className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Personal Information</h4>

                  {/* Name Suggestion Banner */}
                  {showNameSuggestion && (suggestedFirstName || suggestedLastName) && (
                    <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                            Suggested names from {user.email ? 'email' : 'username'}:
                          </p>
                          <div className="flex items-center space-x-2 text-sm text-blue-800 dark:text-blue-200">
                            <span className="font-semibold">{suggestedFirstName || '(empty)'}</span>
                            <span className="text-blue-400">•</span>
                            <span className="font-semibold">{suggestedLastName || '(empty)'}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            type="button"
                            onClick={() => {
                              const temp = suggestedFirstName;
                              setSuggestedFirstName(suggestedLastName);
                              setSuggestedLastName(temp);
                            }}
                            className="p-2 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
                            title="Swap first and last name"
                          >
                            <ArrowLeftRight size={16} className="text-blue-700 dark:text-blue-400" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setFirstName(suggestedFirstName);
                              setLastName(suggestedLastName);
                              setShowNameSuggestion(false);
                            }}
                            className="p-2 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 rounded-lg transition-colors"
                            title="Accept suggestion"
                          >
                            <Check size={16} className="text-green-700 dark:text-green-400" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowNameSuggestion(false)}
                            className="p-2 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-lg transition-colors"
                            title="Discard suggestion"
                          >
                            <XCircle size={16} className="text-red-700 dark:text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">First Name</label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Last Name</label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Last name"
                      />
                    </div>
                  </div>

                  {/* Swap Names Button */}
                  {(firstName || lastName) && (
                    <div className="mt-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          const temp = firstName;
                          setFirstName(lastName);
                          setLastName(temp);
                        }}
                        className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center space-x-1 transition-colors"
                      >
                        <ArrowLeftRight size={14} />
                        <span>Swap first and last name</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Contact Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <PhoneInput label="Mobile Phone" value={mobilePhone} onChange={setMobilePhone} />
                    <PhoneInput label="Business Phone" value={businessPhone} onChange={setBusinessPhone} />
                  </div>
                </div>

                {/* Address Information */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Address</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Street Address</label>
                      <input
                        type="text"
                        value={streetAddress}
                        onChange={(e) => setStreetAddress(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Street address"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Postal Code</label>
                        <input
                          type="text"
                          value={postalCode}
                          onChange={(e) => setPostalCode(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="12345"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">City</label>
                        <input
                          type="text"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="City"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Country</label>
                        <input
                          type="text"
                          value={country}
                          onChange={(e) => setCountry(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Country"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={accountLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {accountLoading ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </form>
          )}

          {/* Work Information Tab */}
          {activeTab === 'work' && (
            <form onSubmit={handleWorkSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Company Name</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Department</label>
                  <input
                    type="text"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Department"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Job Title</label>
                  <input
                    type="text"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Job title"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={accountLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {accountLoading ? 'Saving...' : 'Save Work Info'}
                </button>
              </div>
            </form>
          )}

          {/* Email Tab */}
          {activeTab === 'email' && (
            <form onSubmit={handleEmailSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Email</label>
                  <input
                    type="email"
                    value={user.email}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New Email</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter new email address"
                    required
                  />
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Note:</strong> Changing the email will update the user's login email address.
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={emailLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {emailLoading ? 'Updating...' : 'Update Email'}
                </button>
              </div>
            </form>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <form onSubmit={handlePasswordSubmit}>
              <div className="space-y-4">
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>Admin Override:</strong> This will directly set the user's password without requiring their current password.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter new password"
                    required
                    minLength={8}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Minimum 8 characters</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Confirm new password"
                    required
                    minLength={8}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {passwordLoading ? 'Setting Password...' : 'Set Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
